/**
 * TimePicker Component
 * Google Calendar-like time picker for selecting hours and minutes
 */

import React, { useState, useEffect } from 'react';

interface TimePickerProps {
  selectedTime: Date;
  onTimeSelect: (time: Date) => void;
  onClose: () => void;
  /** Minimum selectable time (for blocking past hours on today) */
  minTime?: Date;
  /** Time interval in minutes (default: 30) */
  interval?: 5 | 15 | 30;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  selectedTime,
  onTimeSelect,
  onClose,
  minTime,
  interval = 30
}) => {
  // Round the selected time to the nearest interval
  const roundToInterval = (date: Date) => {
    const minutes = date.getMinutes();
    const roundedMinutes = Math.ceil(minutes / interval) * interval;
    if (roundedMinutes >= 60) {
      return { hour: date.getHours() + 1, minute: 0 };
    }
    return { hour: date.getHours(), minute: roundedMinutes };
  };

  const initialTime = roundToInterval(selectedTime);
  const [tempHour, setTempHour] = useState(initialTime.hour);
  const [tempMinute, setTempMinute] = useState(initialTime.minute);
  const [is24Hour, setIs24Hour] = useState(true);

  // Generate hours array (filter out past hours if minTime is provided and it's today)
  const generateHours = () => {
    const allHours = is24Hour
      ? Array.from({ length: 24 }, (_, i) => i)
      : Array.from({ length: 12 }, (_, i) => i + 1);

    if (minTime) {
      const minHour = minTime.getHours();
      return allHours.filter(h => h >= minHour);
    }
    return allHours;
  };

  // Generate minutes array based on interval
  const generateMinutes = () => {
    const count = 60 / interval;
    const allMinutes = Array.from({ length: count }, (_, i) => i * interval);

    // If minTime is provided and current hour equals minTime hour, filter out past minutes
    if (minTime && tempHour === minTime.getHours()) {
      const minMinute = Math.ceil(minTime.getMinutes() / interval) * interval;
      return allMinutes.filter(m => m >= minMinute);
    }
    return allMinutes;
  };

  const hours = generateHours();
  const minutes = generateMinutes();

  // Auto-adjust minute when hour changes and current minute is no longer valid
  useEffect(() => {
    if (!minutes.includes(tempMinute)) {
      setTempMinute(minutes[0] ?? 0);
    }
  }, [tempHour, minutes, tempMinute]);

  const handleTimeChange = () => {
    const newDate = new Date(selectedTime);
    newDate.setHours(tempHour, tempMinute, 0, 0);
    onTimeSelect(newDate);
    onClose();
  };

  return (
    <div
      className="absolute z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-4 mt-1 w-64"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Seleccionar Hora</h3>
        <button
          onClick={() => setIs24Hour(!is24Hour)}
          className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          {is24Hour ? '24h' : '12h'}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4">
        {/* Hour Selector */}
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Hora</label>
          <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded">
            {hours.map((hour) => (
              <button
                key={hour}
                onClick={() => setTempHour(hour)}
                className={`w-full px-3 py-1.5 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  tempHour === hour
                    ? 'bg-sky-100 dark:bg-sky-900 text-sky-600 dark:text-sky-400 font-medium'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {hour.toString().padStart(2, '0')}
              </button>
            ))}
          </div>
        </div>

        <div className="text-gray-500 dark:text-gray-400 font-medium">:</div>

        {/* Minute Selector */}
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Minutos</label>
          <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded">
            {minutes.map((minute) => (
              <button
                key={minute}
                onClick={() => setTempMinute(minute)}
                className={`w-full px-3 py-1.5 text-sm text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  tempMinute === minute
                    ? 'bg-sky-100 dark:bg-sky-900 text-sky-600 dark:text-sky-400 font-medium'
                    : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                {minute.toString().padStart(2, '0')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Current time display */}
      <div className="text-center mb-4 p-2 bg-gray-50 dark:bg-gray-700 rounded">
        <div className="text-lg font-mono text-gray-900 dark:text-white">
          {tempHour.toString().padStart(2, '0')}:{tempMinute.toString().padStart(2, '0')}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-between items-center">
        <button
          onClick={onClose}
          className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
        >
          Cancelar
        </button>
        <button
          onClick={handleTimeChange}
          className="px-3 py-1 text-xs bg-sky-600 text-white rounded hover:bg-sky-700"
        >
          Aplicar
        </button>
      </div>
    </div>
  );
};
